import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, ArrowLeft } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { SERVICES } from "@/lib/assets";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.key === slug);
  if (!service) return <Navigate to="/services" replace />;

  const { detail } = service;

  return (
    <div data-testid="service-detail-page">
      {/* Breadcrumb / hero */}
      <section className="relative h-[70vh] overflow-hidden">
        <img src={service.img} alt={service.title} className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/50 to-[#050505]/40" />
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-5 sm:px-8 flex flex-col justify-end pb-16">
          <Link to="/services" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/60 hover:text-primary transition-colors mb-6 w-fit">
            <ArrowLeft size={14} /> Modes of Transport
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">{service.num} · {service.tag}</p>
          <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] sm:text-[9vw] lg:text-[7vw]">
            <RevealText lines={[service.title]} testid="service-detail-title" />
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-[1100px] px-5 sm:px-8 py-24">
        <Reveal>
          <p className="text-xl sm:text-2xl text-white/80 leading-relaxed">{detail.intro}</p>
        </Reveal>

        <div className="mt-20 space-y-20">
          {detail.sections.map((sec, i) => (
            <Reveal key={sec.h} delay={0.05}>
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <span className="font-display text-5xl stroke-text block mb-4">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-tight">{sec.h}</h2>
                </div>
                <div className="lg:col-span-8 space-y-5">
                  {sec.p && sec.p.map((para, pi) => (
                    <p key={pi} className="text-white/60 text-lg leading-relaxed">{para}</p>
                  ))}
                  {sec.list && (
                    <ul className="grid sm:grid-cols-2 gap-3 pt-2">
                      {sec.list.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-white/80">
                          <Check size={16} className="text-primary shrink-0 mt-1.5" /> <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 flex flex-col sm:flex-row sm:items-center justify-between gap-8">
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter max-w-2xl">Ready to move your {service.title.toLowerCase()}?</h2>
          <Link to="/contact" data-testid="service-detail-cta" className="inline-flex items-center gap-3 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest w-fit">
            Get a Quote <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* Other services */}
      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 py-20">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-8">/ Other services</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {SERVICES.filter((s) => s.key !== slug).map((s) => (
            <Link key={s.key} to={`/services/${s.key}`} className="bg-[#050505] p-6 hover:bg-primary group transition-colors duration-300">
              <span className="font-mono text-xs text-primary group-hover:text-white transition-colors">{s.num}</span>
              <p className="font-display text-lg uppercase tracking-tight mt-3 group-hover:text-white transition-colors">{s.title}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
